import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Alert, Button, Result, Spin, Typography } from "antd";
import { signOut } from "../lib/auth-client";
import { getTrpcErrorMessage, tenantApi } from "./api";
import {
  formatTenantLabel,
  isOperatingAwayFromHome,
  isTenantActiveStatus,
  isTenantAdminRole,
  roleLabel,
} from "./access";
import type { TenantContextValue } from "./types";

const { Paragraph } = Typography;

interface TenantContextState {
  context: TenantContextValue | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  switchTenant: (tenantId: string) => Promise<void>;
  isAdmin: boolean;
  isPlatformSuperAdmin: boolean;
  isAwayFromHome: boolean;
  activeTenantLabel: string;
  homeTenantLabel: string;
  roleText: string;
}

const TenantReactContext = createContext<TenantContextState | null>(null);

async function refreshClientAfterTenantSwitch() {
  // Prefer a full reload so all local page state and ad-hoc tRPC caches reset.
  if (typeof window !== "undefined") {
    window.location.reload();
  }
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const [context, setContext] = useState<TenantContextValue | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const next = await tenantApi.context();
      setContext(next);
    } catch (err) {
      setContext(null);
      setError(getTrpcErrorMessage(err, "加载租户上下文失败"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const switchTenant = useCallback(async (tenantId: string) => {
    await tenantApi.switch({ tenantId });
    await refreshClientAfterTenantSwitch();
  }, []);

  const handleLogout = useCallback(async () => {
    await signOut();
  }, []);

  const value = useMemo<TenantContextState>(() => {
    const isPlatformSuperAdmin = Boolean(context?.isPlatformSuperAdmin);
    const isAdmin = isTenantAdminRole(context?.role) || isPlatformSuperAdmin;
    return {
      context,
      loading,
      error,
      reload,
      switchTenant,
      isAdmin,
      isPlatformSuperAdmin,
      isAwayFromHome: isOperatingAwayFromHome(context),
      activeTenantLabel: formatTenantLabel(context?.activeTenant),
      homeTenantLabel: formatTenantLabel(context?.homeTenant),
      roleText: roleLabel(context?.role),
    };
  }, [context, loading, error, reload, switchTenant]);

  if (loading) {
    return (
      <div className="loading-container">
        <Spin size="large" tip="正在加载租户上下文..." />
      </div>
    );
  }

  if (error || !context) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24 }}>
        <Result
          status="error"
          title="无法进入业务系统"
          subTitle={error || "未获取到租户上下文"}
          extra={[
            <Button key="retry" type="primary" onClick={() => void reload()}>
              重试
            </Button>,
            <Button key="logout" onClick={() => void handleLogout()}>
              退出登录
            </Button>,
          ]}
        >
          <Paragraph type="secondary" style={{ textAlign: "center" }}>
            账号由管理员分配并绑定租户。若持续失败，请联系平台管理员。
          </Paragraph>
        </Result>
      </div>
    );
  }

  if (!isTenantActiveStatus(context.status) || !isTenantActiveStatus(context.activeTenant.status)) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24 }}>
        <Result
          status="warning"
          title="当前租户已停用"
          subTitle={`租户「${formatTenantLabel(context.activeTenant)}」当前状态为 ${context.activeTenant.status || context.status}，业务功能已阻断。`}
          extra={[
            <Button key="logout" type="primary" danger onClick={() => void handleLogout()}>
              退出登录
            </Button>,
            <Button key="retry" onClick={() => void reload()}>
              重新检查
            </Button>,
          ]}
        >
          <Alert
            type="warning"
            showIcon
            message="请联系平台管理员恢复租户，或切换到可用租户后再试。"
          />
        </Result>
      </div>
    );
  }

  return (
    <TenantReactContext.Provider value={value}>{children}</TenantReactContext.Provider>
  );
}

export function useTenantContext() {
  const value = useContext(TenantReactContext);
  if (!value) {
    throw new Error("useTenantContext 必须在 TenantProvider 内使用");
  }
  return value;
}

export function useOptionalTenantContext() {
  return useContext(TenantReactContext);
}
